import { useEffect, useState } from 'react';
import { notification, Popover } from 'antd';
import moment from 'moment-timezone';
import APIClient from '../api/api';
import { AutoRenewReviewEntry, ListAutoRenewReviewResponse } from '../api/models';
import { Button, Column, DataTable, DomainName, Pagination, StatusTag, TagTone } from '../components/ui';
import { PageFooter, PageHead, PageToolbar } from '../layout';
import { SelectPill } from '../components/FilterPills';

const DEFAULT_PAGE_SIZE = 25;

const AUCTION_PUSH_DEADLINE_DAYS = 22;

const statusOptions: { value: string; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'reviewed_reenabled', label: 'Re-Enabled' },
  { value: 'reviewed_keep_off', label: 'Kept Off' },
  { value: 'expired_unreviewed', label: 'Expired (unreviewed)' },
  { value: 'all', label: 'All' },
];

const statusTone: Record<string, TagTone> = {
  open: 'warn',
  reviewed_reenabled: 'success',
  reviewed_keep_off: 'neutral',
  expired_unreviewed: 'danger',
};

type SortKey = 'expires_at' | 'queued_at' | 'domain';
type SortDir = 'asc' | 'desc';

const AutoRenewReview = () => {
  const [data, setData] = useState<ListAutoRenewReviewResponse>();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [statusFilter, setStatusFilter] = useState<string>('open');
  const [sortBy, setSortBy] = useState<SortKey>('expires_at');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [actingId, setActingId] = useState<number | null>(null);

  useEffect(() => {
    load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, statusFilter, sortBy, sortDir]);

  async function load(p: number) {
    setLoading(true);
    try {
      const resp = await APIClient.listAutoRenewReview(
        { page: p, pageSize },
        { status: statusFilter, sortBy, sortDir },
      );
      setData(resp);
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to load review queue.' });
    } finally {
      setLoading(false);
    }
  }

  async function reenable(entry: AutoRenewReviewEntry) {
    setActingId(entry.id);
    try {
      await APIClient.reenableAutoRenewReview(entry.id);
      notification.success({ message: `Auto-renew re-enabled for ${entry.domain}` });
      load(page);
    } catch (e: any) {
      notification.error({ message: e?.response?.data?.error || 'Re-enable failed' });
    } finally {
      setActingId(null);
    }
  }

  async function keepOff(entry: AutoRenewReviewEntry) {
    setActingId(entry.id);
    try {
      await APIClient.keepOffAutoRenewReview(entry.id);
      notification.success({ message: `${entry.domain} marked reviewed (kept off)` });
      load(page);
    } catch (e: any) {
      notification.error({ message: e?.response?.data?.error || 'Action failed' });
    } finally {
      setActingId(null);
    }
  }

  function toggleSort(key: SortKey) {
    if (sortBy === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
    setPage(1);
  }

  function dirFor(key: SortKey): SortDir | undefined {
    return sortBy === key ? sortDir : undefined;
  }

  function daysUntil(date: Date): number {
    return Math.ceil((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  }

  function pushDeadline(entry: AutoRenewReviewEntry): Date {
    return new Date(entry.expiresAt.getTime() + AUCTION_PUSH_DEADLINE_DAYS * 24 * 60 * 60 * 1000);
  }

  const statusLabel = statusOptions.find((o) => o.value === statusFilter)?.label || 'Open';

  const columns: Column<AutoRenewReviewEntry>[] = [
    {
      key: 'domain',
      title: 'Domain',
      sortable: true,
      sortDirection: dirFor('domain'),
      onSort: () => toggleSort('domain'),
      render: (e) => <DomainName name={e.domain} />,
    },
    {
      key: 'expiresAt',
      title: 'Expires',
      sortable: true,
      sortDirection: dirFor('expires_at'),
      onSort: () => toggleSort('expires_at'),
      render: (e) => (
        <span className="font-mono text-[12.5px] text-text-2 tabular-nums" title={moment(e.expiresAt).format('LLL')}>
          {moment(e.expiresAt).format('YYYY-MM-DD')}
        </span>
      ),
    },
    {
      key: 'daysLeft',
      title: 'Days Left',
      align: 'right',
      render: (e) => {
        const d = daysUntil(e.expiresAt);
        const tone = d < 0 ? 'text-danger' : d < 7 ? 'text-warn' : 'text-text-2';
        return <span className={`font-mono text-[12.5px] tabular-nums ${tone}`}>{d}</span>;
      },
    },
    {
      key: 'daysToPush',
      title: 'Days to Push',
      align: 'right',
      render: (e) => {
        const d = daysUntil(pushDeadline(e));
        const tone = d < 0 ? 'text-danger' : d < 7 ? 'text-warn' : 'text-text-2';
        return (
          <span
            className={`font-mono text-[12.5px] tabular-nums ${tone}`}
            title={`Must be in atticexpired by ${moment(pushDeadline(e)).format('YYYY-MM-DD')} to make the auction`}
          >
            {d}
          </span>
        );
      },
    },
    {
      key: 'reason',
      title: 'Reason',
      render: (e) => (
        <Popover
          trigger="click"
          getPopupContainer={() => document.body}
          content={
            <div className="max-w-[420px] p-2">
              <div className="text-xs text-text mb-2 whitespace-pre-wrap">{e.reason}</div>
              <pre className="text-2xs font-mono text-text-2 bg-bg p-2 rounded-md border border-border max-h-[260px] overflow-auto m-0">
                {JSON.stringify(e.decisionData, null, 2)}
              </pre>
            </div>
          }
        >
          <button type="button" className="text-[12.5px] text-text-2 text-left hover:text-text truncate max-w-[360px] block">
            {e.reason}
          </button>
        </Popover>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      width: 120,
      render: (e) => <StatusTag tone={statusTone[e.status] ?? 'neutral'}>{statusLabelOf(e.status)}</StatusTag>,
    },
    {
      key: 'queuedAt',
      title: 'Queued',
      width: 120,
      sortable: true,
      sortDirection: dirFor('queued_at'),
      onSort: () => toggleSort('queued_at'),
      render: (e) => (
        <span className="text-2xs text-text-3 font-mono">{moment(e.queuedAt).fromNow()}</span>
      ),
    },
    {
      key: 'reviewedBy',
      title: 'Reviewed',
      render: (e) =>
        e.reviewedAt ? (
          <span className="text-2xs text-text-3 font-mono">
            {e.reviewedAction === 'reenable' ? '↺' : '✓'} {e.reviewedBy || '—'}
            {' · '}
            {moment(e.reviewedAt).fromNow()}
          </span>
        ) : (
          <span className="text-2xs text-text-3">—</span>
        ),
    },
    {
      key: 'actions',
      title: '',
      align: 'right',
      width: 240,
      render: (e) =>
        e.status === 'open' ? (
          <div className="inline-flex gap-1.5">
            <Button
              size="sm"
              variant="primary"
              loading={actingId === e.id}
              onClick={() => reenable(e)}
            >
              Re-Enable Auto Renew
            </Button>
            <Button size="sm" variant="ghost" loading={actingId === e.id} onClick={() => keepOff(e)}>
              Keep OFF
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <PageHead
        title="Auto-Renew Review"
        subtitle="Domains where the system auto-disabled auto-renew based on profitability rules. Re-enable or acknowledge before they expire."
      />
      <PageToolbar>
        <SelectPill
          label="Status"
          value={statusLabel}
          options={statusOptions}
          selected={statusFilter}
          onChange={(v) => {
            setStatusFilter(v || 'open');
            setPage(1);
          }}
        />
        <span className="ml-auto text-2xs text-text-3 font-mono">
          {data ? (
            <>total <b className="text-text-2">{data.total.toLocaleString()}</b></>
          ) : (
            'loading…'
          )}
        </span>
      </PageToolbar>

      <DataTable
        columns={columns}
        data={data?.content ?? []}
        rowKey={(e) => String(e.id)}
        loading={loading}
        emptyMessage="No review entries."
      />

      <PageFooter
        left={
          <span>
            Showing <b className="text-text-2">{data?.content.length ?? 0}</b> of{' '}
            <b className="text-text-2">{data?.total.toLocaleString() ?? 0}</b>
          </span>
        }
        right={
          <Pagination
            page={page}
            pageSize={pageSize}
            total={data?.total ?? 0}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(1);
            }}
            loading={loading}
          />
        }
      />
    </>
  );
};

function statusLabelOf(status: string): string {
  switch (status) {
    case 'open': return 'Open';
    case 'reviewed_reenabled': return 'Re-Enabled';
    case 'reviewed_keep_off': return 'Kept Off';
    case 'expired_unreviewed': return 'Expired';
    default: return status;
  }
}

export default AutoRenewReview;
